import {useContainer} from "#imports";
import {DependencyInjectId} from "@@/package/const/DependencyInjectId";
import LevelAssessmentMapper from "~~/package/mapper/levelAssessment/LevelAssessmentMapper";
import {LevelAssessmentModel} from "~~/package/mapper/levelAssessment/model/LevelAssessmentModel";
import {TaskModel} from "~~/package/mapper/learning/model/TaskModel";
import type LevelAssessmentSubmitRequest from "~~/package/apis/levelAssessment/request/LevelAssessmentSubmitRequest";
import {LevelAssessmentResultListModel} from "~~/package/mapper/levelAssessment/model/LevelAssessmentResultListModel";
import type LevelAssessmentIdListResponse from "~~/package/apis/levelAssessment/response/LevelAssessmentIdListResponse";
import {useLearningService} from "~/composables/service/useLearningService";
import {TaskType} from "~~/package/mapper/learning/model/TaskType";
import {useFaroTracking} from "~/composables/useFaroTracking";

export const useLevelAssessmentService = () => {
    const levelAssessmentMapper = useContainer<LevelAssessmentMapper>(DependencyInjectId.LevelAssessmentMapper);
    const faroTracking = useFaroTracking();

    const getLevelAssessmentBasicInfo = (levelId: number): Promise<LevelAssessmentModel> => {
        return levelAssessmentMapper.getLevelAssessmentBasicInfo(levelId);
    };

    const getLevelAssessmentInfo = async (levelId: number, isWithStatusData = true): Promise<LevelAssessmentModel> => {
        const basicInfo = await levelAssessmentMapper.getLevelAssessmentBasicInfo(levelId);
        if(isWithStatusData) {
            const statusInfo = await levelAssessmentMapper.getLevelAssessmentInfo(levelId);
            return LevelAssessmentModel.dataMerging(basicInfo, statusInfo);
        } else {
            return basicInfo;
        }
    };

    const getLevelAssessmentIdList = (levelId: number, status: string): Promise<LevelAssessmentIdListResponse> => {
        return levelAssessmentMapper.getLevelAssessmentIdList(levelId, status);
    };

    const getLevelAssessmentTask = (taskTemplateId: number, levelId: number): Promise<TaskModel> => {
      return levelAssessmentMapper.getLevelAssessmentTask(taskTemplateId, levelId);
    };

    const getLevelAssessmentTasksWithLatestCodes = async (taskTemplateId: number, levelId: number): Promise<TaskModel> => {
        const taskData = getLevelAssessmentTask(taskTemplateId, levelId);

        if (([TaskType.CODE, TaskType.MULTI_FILE_CODE, TaskType.CODE_WITHOUT_RUN] as TaskType[]).includes((await taskData).type as TaskType))
            (await taskData).submitCode = await useLearningService().getLatestSavedCode((await taskData).id, "level_assessment", levelId);
        return taskData;
    }

    // ASMT-TYPE-03: Level Assessment 답안 제출(마지막 문항 제출 시 서버가 passScore로 Pass 판정)
    const submitAnswer = (levelId: number, taskTemplateId: number, submitData: LevelAssessmentSubmitRequest): Promise<boolean> => {
        return levelAssessmentMapper.submitAnswer(levelId, taskTemplateId, submitData)
            .then(result => {
                if (result) faroTracking.trackProblemSubmitted({context: 'level', assessmentId: levelId, templateId: taskTemplateId, isSample: false});
                return result;
            });
    }

    const getLevelAssessmentResultList = (levelId :number): Promise<LevelAssessmentResultListModel[]> => {
        return levelAssessmentMapper.getLevelAssessmentResultList(levelId);
    }

    return {
        getLevelAssessmentBasicInfo,
        getLevelAssessmentInfo,
        getLevelAssessmentIdList,
        getLevelAssessmentTask,
        getLevelAssessmentTasksWithLatestCodes,
        submitAnswer,
        getLevelAssessmentResultList
    };
}
